import { AfterViewInit, ChangeDetectorRef, Component, forwardRef, Input, OnInit, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Router } from '@angular/router';
import { ReuseTabService } from '@delon/abc/reuse-tab';
import { _HttpClient } from '@delon/theme';
import { NzSelectComponent } from 'ng-zorro-antd/select';

import { CustomerAddConfigService } from '../../inline-add-panel/customer-add-config.service';
import { InlineAddPanelComponent } from '../../inline-add-panel/inline-add-panel.component';
import { SelectBaseComponent } from '../select-base';
import { SelectInstantBaseComponent } from '../selectInstant-base';
import { SelectInstantBaseNewComponent } from '../selectInstant-base-new';

@Component({
  selector: 'app-term-select',
  templateUrl: './term-select.component.html',
  styles: [
    `
      ::ng-deep .ant-select-item-option-content .special-option {
        text-align: center !important;
      }

      .term-select-container {
        position: relative;
        width: 100%;
      }

      ::ng-deep .add-new-button {
        color: #000000 !important;
        font-weight: 500 !important;
        width: 100% !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        gap: 2px !important;
        background-color: transparent !important;
        border: none !important;
      }

      ::ng-deep .add-new-button .anticon {
        color: #000000 !important;
      }

      ::ng-deep .dropdownSelect:not(:has(.ant-select-item-option-active))
        .ant-select-item-option:nth-child(2):not(.ant-select-item-option-active):not(.ant-select-item-option-selected):not(:hover) {
        background-color: #ffc000;
      }
      ::ng-deep .dropdownSelect
        .ant-select-item-option:first-child:last-child:not(.ant-select-item-option-active):not(.ant-select-item-option-selected):not(:hover) {
        background-color: #ffc000;
      }
    `
  ],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TermSelectComponent),
      multi: true
    }
  ]
})
export class TermSelectComponent extends SelectInstantBaseNewComponent implements OnInit, AfterViewInit {

  @ViewChild('selector') nzSelectComponent!: NzSelectComponent;

  @Input() tabindex: string | null = null;
  NotShowInReport: boolean = false;
  termConfig: any;
  inlineDefaults: any = null;

  constructor(
    http: _HttpClient,
    router: Router,
    cdr: ChangeDetectorRef,
    private resueTabService: ReuseTabService,
    private customerAddConfigService: CustomerAddConfigService
  ) {
    super(http, cdr, router);
  }

  ngOnInit(): void {
    this.checkAddNewOptionInReportAccess();
    this.termConfig = this.customerAddConfigService.getTermsConfig();
    
    // Listen to search form changes to store instant data
    this.searchForm.controls.keyword.valueChanges.subscribe(value => {
      if (value) {
        this.storedInstantData = value.trim();
        // Also store in pendingInstantData for Add New (handled by base class)
        this.pendingInstantData = value.trim();
      }
    });

    // Ensure the correct item is active whenever the list updates while open
    this.onListFilled.subscribe(() => {
      if ((this.nzSelectComponent as any)?.nzOpen) {
        this.ensureFirstItemActivatedAndFocused();
      }
    });
  }

  getUrl(searchValue: string): string {
    let url = `api/terms/lookup?$top=50&$skip=0&$orderby=code asc`;
    if (searchValue) {
      const searchStr = searchValue.replaceAll("'", "''");
      url = `${url}&$filter=(contains(code, '${encodeURIComponent(
        searchStr
      )}') or contains(description,'${encodeURIComponent(searchStr)}'))`;
    }
    return url;
  }

  // set the location want to navigate
  jump(): string {
    return '/company/term';
  }

  // set the param
  queryParam(): String {
    return 'TermCode';
  }

  checkAddNewOptionInReportAccess(): boolean {
    let curUrl = this.resueTabService.curUrl.toLowerCase();

    if (curUrl.includes("report")) {
      this.NotShowInReport = true;
    } else {
      this.NotShowInReport = false;
    }

    return this.NotShowInReport;
  }
  ngAfterViewInit(): void {
    if (this.tabIndex && this.selector) {
      const selectorEl = this.selector.originElement.nativeElement;

      const searchInput = selectorEl.querySelector('input');

      if (searchInput) {
        searchInput.setAttribute('tabindex', this.tabIndex?.toString() || '0');
        
        // Listen to input events directly
        searchInput.addEventListener('input', (event: any) => {
          if (event.target.value) {
            const trimmedValue = event.target.value.trim();
            this.storedInstantData = trimmedValue;
            // Also store in pendingInstantData for Add New (handled by base class)
            this.pendingInstantData = trimmedValue;
          }
        });
      }
    }
  }

  // Override search method to store instant data
  override search(value: string, maxLength: number = 10): void {
    // Store the instant data before calling parent method
    this.storedInstantData = value.trim();
    super.search(value, maxLength);
  }

  // Override setKeyword method to store instant data
  override setKeyword(value: string, maxLength: number = 10): void {
    // Store the instant data before calling parent method
    this.storedInstantData = value.trim();
    super.setKeyword(value, maxLength);
  }

  
  // Override the addNew method to show inline panel instead of navigation
  override addNew(): void {
    // Use pendingInstantData which was captured synchronously in base class onAddNewClick
    // This ensures we have the data even if storedInstantData was cleared
    const instantDataToUse = this.pendingInstantData || this.storedInstantData;
    
    // If we still don't have data, try to capture it
    if (!instantDataToUse) {
      this.captureCurrentSearchValue();
      const currentSearchValue = this.getCurrentSearchValue();
      if (currentSearchValue) {
        this.pendingInstantData = currentSearchValue;
      }
    }
    
    // Final fallback - use whatever we have
    const finalInstantData = this.pendingInstantData || this.storedInstantData || '';
    
    // Load options and defaults first, then show panel
    Promise.all([
      this.loadSelectOptions(),
      this.fetchInlineDefaults()
    ]).then(() => {
      this.showInlineAddPanel = true;
      // Force change detection to ensure the panel is rendered
      this.cdr.detectChanges();
      
      // Use setTimeout to ensure the panel is fully rendered before calling changeInstantData
      setTimeout(() => {
        if (this.inlineAddPanel && finalInstantData) {
          this.inlineAddPanel.changeInstantData(finalInstantData);
        }
      }, 0);
      
      if (this.nzSelectComponent) {
        this.nzSelectComponent.setOpenState(false);
      }
    });
  }

  onTermCreated(event: { item: any; selectionConfig?: any }): void {
    if (event.item && event.selectionConfig) {
      // Create the term object in the expected format
      const term = {
        id: event.item[event.selectionConfig.valueField],
        code: event.item.code,
        description: event.item[event.selectionConfig.labelField],
        value: event.item.value,
        type: event.item.type,
        isDefault: event.item.isDefault || false
      };

      // Add the new term to the list and select it
      this.list.unshift(term);

      // Close the inline panel first
      this.showInlineAddPanel = false;

      // Set the term object in the form control to trigger term.valueChanges
      this.value = term.id;
      this.selected = term.id;

      // Set the term object directly in the form control
      this.onChange(term);
      this.writeValue(term);
      
      // Clear stored instant data after successful creation
      this.clearStoredInstantData();
      
      // Force change detection
      this.cdr.detectChanges();
    }
  }

  onTermCancelled(): void {
    this.showInlineAddPanel = false;
    this.clearStoredInstantData();
  }


  onTermMoreDetails(): void {
    // Navigate to term details page with all the current form data
    const formData = this.inlineAddPanel?.formGroup?.value;
    if (formData) {
      // Create query params with all form fields
      const queryParams: any = {
        autoPopulate: 1
      };
      
      // Add all form fields to query params
      if (formData.code) queryParams.TermCode = formData.code;
      if (formData.description) queryParams.Description = formData.description;
      if (formData.value) queryParams.Value = formData.value;
      if (formData.type !== undefined) queryParams.Type = formData.type;
      if (formData.isDefault !== undefined) queryParams.IsDefault = formData.isDefault;

      queryParams.autoPopulate = 1;
      
      this.router.navigate(['/company/term'], {
        queryParams: queryParams,
      });
    } else {
      this.router.navigate(['/company/term'], {
        queryParams: {
          autoPopulate: 1
        }
      });
    }
    this.showInlineAddPanel = false;
    this.clearStoredInstantData();
  }

  onFieldChange(event: { fieldName: string; value: any }): void {
    // Handle field changes - auto-code component will handle code generation
  }

  private loadSelectOptions(): Promise<void> {
    // For terms, we don't need to load additional options like customers do
    return Promise.resolve();
  }

  private fetchInlineDefaults(): Promise<void> {
    // Call server to get defaults similar to term/detail new()
    return new Promise((resolve) => {
      this.http.get('api/terms/new').subscribe({
        next: (data: any) => {
          const defaults: any = {};
          if (data?.code) defaults.code = data.code;
          if (data?.description) defaults.description = data.description;
          if (data?.value) defaults.value = data.value;
          if (data?.type !== undefined) defaults.type = data.type;
          if (data?.isDefault !== undefined) defaults.isDefault = data.isDefault;
          
          this.inlineDefaults = defaults;
          resolve();
        },
        error: () => {
          this.inlineDefaults = null;
          resolve();
        }
      });
    });
  }

}
